'use client';

import {
  Box,
  Grid, GridItem
} from '@chakra-ui/react';

import { EAgentExecutionStatus, EAgentSchemaType, EAgentService, TAgentProperty, TSocketAgentLog, isErrorLog } from '@/types/agents';

import ExecutionLogPanel from './components/ExecutionLogPanel/ExecutionLogPanel';
import ExecutionLogsHeader from './components/ExecutionLogsHeader/ExecutionLogsHeader';
import ResultCanvas from './components/ResultCanvas/ResultCanvas';
import useExecutionLogsPage from './useExecutionLogsPage';

interface IExecutionLogsPageProps {
  isExecuting?: boolean;
  agentId?: string;
  agentConfigId?: string;
  agentSchema?: Record<string, { type: EAgentSchemaType, description: string }>;
  setIsExecuting?: (isExecuting: boolean) => void;
  properties?: TAgentProperty[];
  agentService?: EAgentService;
}

const ExecutionLogsPage = ({
  isExecuting,
  agentId,
  agentConfigId,
  agentSchema,
  setIsExecuting,
  properties,
  agentService
}: IExecutionLogsPageProps) => {

  const {
    socketLogs,
    logs,
    handleBackButtonClick,
    handleRetryClick,
    isLoadingRunAgent
  } = useExecutionLogsPage({ isExecuting, agentId, agentConfigId, setIsExecuting, properties, agentService });

  const lastRunAgentLog = socketLogs.filter((l): l is TSocketAgentLog => !isErrorLog(l)).pop();

  const isImagePresent = lastRunAgentLog?.output?.image;
  const isResponsePresent = lastRunAgentLog?.output?.response;
  const isFilePresent = lastRunAgentLog?.output?.file;

  const isSocketLogError = socketLogs.some((log) => isErrorLog(log) || log.status === EAgentExecutionStatus.FAILED);

  const isCanvasLoading = isLoadingRunAgent || !logs.length || (!isImagePresent && !isResponsePresent && !isFilePresent);

  const logMetadata = lastRunAgentLog
    ? { output: lastRunAgentLog.output }
    : null;

  return (
    <Box
      minH={'calc(100dvh - var(--app-header-h))'}
      maxH={'calc(100dvh - var(--app-header-h))'}
      bg="whiteAlpha.0"
    >
      <Grid
        templateColumns={{ base: '1fr', lg: '2fr 1.2fr' }}
        height={'calc(100dvh - var(--app-header-h))'}
      >
        <GridItem p={6} overflow="auto">
          <Box mb={30}>
            <ExecutionLogsHeader
              title="Back to Agent Details"
              onClickBackButton={handleBackButtonClick}
            />
          </Box>
          <ResultCanvas
            metadata={logMetadata}
            isLoading={isCanvasLoading}
            agentSchema={agentSchema || {}}
            error={isSocketLogError}
          />
        </GridItem>
        <ExecutionLogPanel
          logs={logs}
          onCloseClick={handleBackButtonClick}
          onRetryClick={handleRetryClick}
          isLoadingRunAgent={isLoadingRunAgent}
        />
      </Grid>
    </Box>
  );
};

export default ExecutionLogsPage;
